import { useEffect, useRef } from 'react';

interface VisiblePollingOptions {
  enabled?: boolean;
  /** Also call once as soon as polling starts, or on first becoming visible if mounted hidden. */
  immediate?: boolean;
}

/**
 * Calls `callback` every `intervalMs`, but only while the page is visible.
 *
 * A hidden tab stops polling entirely. Coming back calls once straight away if a tick
 * was missed, then resumes the interval — so live data is fresh on return without a
 * background tab keeping the backend awake all night.
 */
export function useVisiblePolling(
  callback: () => void,
  intervalMs: number,
  { enabled = true, immediate = false }: VisiblePollingOptions = {},
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;

    let timer: number | undefined;
    // Mounted in a background tab (e.g. Ctrl-click): owe the immediate call on first return.
    let lastRunAt = immediate && document.visibilityState !== 'visible' ? 0 : Date.now();
    const run = () => {
      lastRunAt = Date.now();
      callbackRef.current();
    };
    const start = () => {
      if (timer === undefined) timer = window.setInterval(run, intervalMs);
    };
    const stop = () => {
      window.clearInterval(timer);
      timer = undefined;
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        stop();
        return;
      }
      if (Date.now() - lastRunAt >= intervalMs) run();
      start();
    };

    if (document.visibilityState === 'visible') {
      if (immediate) run();
      start();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, immediate, intervalMs]);
}
